import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  UserGroupIcon,
  CurrencyDollarIcon,
  PercentBadgeIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useLanguage } from '../../contexts/LanguageContext';
import SettingsInfoNotice from './SettingsInfoNotice';

const TrainerSettings = ({ settings = {}, onSettingsChange }) => {
  const { t } = useLanguage();
  const [commissionRate, setCommissionRate] = useState(
    settings?.teacherCommissionRate != null ? settings.teacherCommissionRate * 100 : 40
  );

  useEffect(() => {
    if (settings?.teacherCommissionRate != null) {
      setCommissionRate(settings.teacherCommissionRate * 100);
    }
  }, [settings?.teacherCommissionRate]);

  const handleCommissionRateChange = (value) => {
    const newRate = parseFloat(value);
    if (Number.isNaN(newRate) || newRate < 0 || newRate > 100) {
      toast.error(t('settingsPage.trainersCommissionInvalid'));
      return;
    }

    setCommissionRate(newRate);

    const updatedSettings = {
      ...settings,
      teacherCommissionRate: newRate / 100,
    };

    if (onSettingsChange) {
      onSettingsChange(updatedSettings);
      toast.success(t('settingsPage.trainersCommissionUpdated', { rate: newRate }));
    }
  };

  return (
    <div className="space-y-6">
      <SettingsInfoNotice variant="info" title={t('settingsPage.trainersCommissionNoticeTitle')}>
        <p>{t('settingsPage.trainersCommissionNotice')}</p>
      </SettingsInfoNotice>

      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-2xl border border-slate-200 bg-white p-6 shadow-soft dark:border-slate-700 dark:bg-slate-900/75"
      >
        <h3 className="mb-4 flex items-center gap-2 text-lg font-semibold text-slate-900 dark:text-slate-50">
          <PercentBadgeIcon className="h-5 w-5 text-purple-400" />
          {t('settingsPage.trainersCommissionTitle')}
        </h3>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
              {t('settingsPage.trainersCommissionLabel')}
            </label>
            <div className="relative">
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={commissionRate}
                onChange={(e) => handleCommissionRateChange(e.target.value)}
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 pr-10 text-slate-900 focus:border-forma-500 focus:outline-none focus:ring-2 focus:ring-forma-500/20 dark:border-slate-600 dark:bg-slate-800/80 dark:text-white"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-500">%</span>
            </div>
            <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
              {t('settingsPage.trainersCommissionHint')}
            </p>
          </div>

          <div className="rounded-xl bg-slate-50 p-4 dark:bg-slate-800/40">
            <p className="mb-3 text-sm font-medium text-slate-700 dark:text-slate-300">
              {t('settingsPage.trainersCommissionPreview')}
            </p>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-600 dark:text-slate-400">{t('settingsPage.trainersPreviewPayment')}</span>
                <span className="text-slate-900 dark:text-slate-50">55 000 DA</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600 dark:text-slate-400">
                  {t('settingsPage.trainersPreviewTrainer', { rate: commissionRate })}
                </span>
                <span className="font-medium text-green-600 dark:text-green-400">
                  {Math.round(55000 * (commissionRate / 100)).toLocaleString('fr-FR')} DA
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600 dark:text-slate-400">
                  {t('settingsPage.trainersPreviewSchool', { rate: (100 - commissionRate).toFixed(1) })}
                </span>
                <span className="font-medium text-blue-600 dark:text-blue-400">
                  {Math.round(55000 * ((100 - commissionRate) / 100)).toLocaleString('fr-FR')} DA
                </span>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05 }}
        className="rounded-2xl border border-slate-200 bg-slate-50/80 p-6 dark:border-slate-700 dark:bg-slate-800/40"
      >
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <UserGroupIcon className="mt-0.5 h-5 w-5 shrink-0 text-blue-400" />
            <div>
              <p className="font-medium text-slate-900 dark:text-slate-50">
                {t('settingsPage.trainersManageTitle')}
              </p>
              <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                {t('settingsPage.trainersManageDesc')}
              </p>
            </div>
          </div>
          <Link
            to="/trainers"
            className="inline-flex shrink-0 items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
          >
            {t('settingsPage.trainersManageLink')}
            <ArrowTopRightOnSquareIcon className="h-4 w-4" />
          </Link>
        </div>
      </motion.div>
    </div>
  );
};

export default TrainerSettings;
